/* eslint-disable react/jsx-key */
import React, { useEffect, useMemo, useState } from "react";
import { TaskType, ImageSelectMultipleStepsTask, ImageSelectSingleStepAnswer } from "data-models/task-models";
import { QUESTION_ACTIONS, useFollowUp } from "providers/FollowUpProvider";
import {
  Tab,
  Tabs,
  Flex,
  Image,
  VStack,
  HStack,
  Center,
  TabList,
  TabPanel,
  TabPanels,
  Box,
  Text,
  RadioGroup,
  Radio,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import CheckIcon from "common/CheckIcon";
import { apiBase } from "services/config";
import { MultiTaskHeading } from "./Common/TaskHeading";

export default function ImageSelectMultipleStepsCmp() {
  const { t, i18n } = useTranslation();
  const { currentTask, dispatch } = useFollowUp();
  const task = currentTask as ImageSelectMultipleStepsTask;

  const [animatingId, setAnimatingId] = useState<ImageSelectSingleStepAnswer["id"] | null>(null);

  const imagesPerStep = Math.max(1, Number(task.imagesPerStep ?? 1));
  const imageOptions = useMemo(
    () =>
      (task.answerOptions ?? []).filter(
        (ao: any) =>
          ao.type === TaskType.ImageSelectMultipleSteps && typeof ao.imageUrl === "string" && ao.imageUrl.length > 0,
      ),
    [task.answerOptions],
  );

  const totalSteps = Math.max(1, Math.ceil(imageOptions.length / imagesPerStep));

  const currentStepIndex = Math.min(Math.max(0, Number(currentTask.step ?? 0)), totalSteps - 1);

  const start = currentStepIndex * imagesPerStep;
  const end = start + imagesPerStep;

  const visibleOptions = useMemo(() => imageOptions.slice(start, end), [imageOptions, start, end]);

  useEffect(() => {
    if (Number(currentTask.step ?? 0) !== currentStepIndex) {
      dispatch({ type: QUESTION_ACTIONS.SET_CURRENT_STEP, payload: currentStepIndex });
    }
  }, [totalSteps]);

  function onSelectedChange(changeId: string) {
    const wasChecked = task.answerOptions.find((ao: any) => ao.id === changeId)?.checked;

    // ids that belong to the current page
    const visibleIds = new Set(visibleOptions.map((v: any) => v.id));

    const newOptionsState = task.answerOptions.map((ao: any) => {
      // only enforce single-select inside the current page
      if (!visibleIds.has(ao.id)) return ao;

      // clicked item: toggle
      if (ao.id === changeId) return { ...ao, checked: !ao.checked };

      // other items on the same page: always uncheck
      return { ...ao, checked: false };
    });

    // animation logic (only if we turned it ON)
    if (!wasChecked) {
      setAnimatingId(changeId);
      window.setTimeout(() => setAnimatingId(null), 300);
    } else {
      setAnimatingId(null);
    }

    dispatch({
      type: QUESTION_ACTIONS.UPDATE_ANSWERS,
      payload: newOptionsState,
    });
    dispatch({ type: QUESTION_ACTIONS.UNSKIP_CURRENT_TASK });
  }

  const onChangeTab = (index: number) => {
    dispatch({ type: QUESTION_ACTIONS.SET_CURRENT_STEP, payload: index });
  };

  // step 0..3 => a..d
  const instructionKey = (idx: number) => (["a", "b", "c", "d"][idx] ?? "a") as "a" | "b" | "c" | "d";

  const localizedStepInstruction = (() => {
    const key = instructionKey(currentStepIndex);
    const arr = (task.taskInstructions as any)?.[key] as [string, string, string] | undefined;

    if (!arr) return "";
    if (i18n.language === "en") return arr[1] ?? arr[0];
    if (i18n.language === "de") return arr[2] ?? arr[0];
    return arr[0];
  })();

  return (
    <VStack pos="relative" h="full" w="full" p="5">
      <MultiTaskHeading description={task.description} headingTitle={task.title} />

      <Tabs h="full" w="full" pos="relative" variant="unstyled" onChange={onChangeTab} index={currentStepIndex}>
        <TabList pb="4" pt="3">
          <Flex
            h="0"
            w="full"
            pos="absolute"
            alignSelf="center"
            justifySelf="center"
            borderTop="1px solid #2A0F74"
            zIndex={0}
          />

          {Array.from({ length: totalSteps }, (_, i) => (
            <Tab
              key={i}
              px={4}
              py={2}
              mr={6}
              zIndex={1}
              w="180px"
              border="1px solid #2A0F74"
              fontSize="xs"
              borderRadius="8px"
              bgColor="#6c40f6"
              _selected={{ bgColor: "#2A0F74", fontWeight: "700" }}
              _focus={{ boxShadow: "none" }}
            >
              <span style={{ opacity: 0.5, paddingRight: "10px" }}>
                {t("Step")} {i + 1}
              </span>
            </Tab>
          ))}
        </TabList>

        <TabPanels h="full" w="full" py={0}>
          {Array.from({ length: totalSteps }, (_, stepIdx) => (
            <TabPanel key={stepIdx} w="full" h="full" maxH="full">
              <HStack w="full" h="100%" justify="center" align="center" mt="-20px">
                {(stepIdx === currentStepIndex ? visibleOptions : []).map((ao: any) => (
                  <VStack key={ao.id} align="center" justify="center" h={{ base: "auto", lg: "50%" }} w="unset">
                    <Center
                      w={task.imagesPerStep === 1 ? "450px" : "260px"}
                      h={task.imagesPerStep === 1 ? "300px" : "200px"}
                      pos="relative"
                      bgColor="white"
                      borderRadius="xl"
                      borderWidth="4px"
                      borderColor={ao.checked ? "pink.500" : "transparent"}
                      onClick={() => onSelectedChange(ao.id)}
                      cursor="pointer"
                      role="button"
                      tabIndex={0}
                    >
                      <Center
                        pos="absolute"
                        left="1"
                        top="1"
                        boxSize="6"
                        bgColor={ao.checked ? "pink.500" : "lightgray"}
                        borderRadius="full"
                      >
                        <CheckIcon color="white" size="16px" />
                      </Center>
                      <Image src={apiBase + ao.imageUrl} maxH="100%" maxW="100%" />
                    </Center>
                  </VStack>
                ))}
              </HStack>
            </TabPanel>
          ))}
        </TabPanels>
      </Tabs>
      <VStack w="full" justifyContent="center" align="stretch" pos="relative">
        <HStack align="start">
          <Text lineHeight="18px" fontSize="sm" fontWeight="bold" whiteSpace="nowrap">
            {t("Note_Text")}:
          </Text>
          <Box lineHeight="18px" fontSize="sm" dangerouslySetInnerHTML={{ __html: localizedStepInstruction }} />
        </HStack>
      </VStack>
    </VStack>
  );
}
